<script lang="ts">
export default {
    name: 'BaseStatusBarExpand',
};
</script>

<script lang="ts" setup>
import { computed, watch, ref } from 'vue-demi';

import ExpandStatus from './components/expand-status.vue';
import FoldStatus from './components/fold-status.vue';
import PageTitles from './components/page-titles.vue';
import { useScroll, useTopGap } from './hooks';

interface Props {
    /**
     * 标题，挂件状态下，只有在收起的时候才会显示标题
     */
    title?: string;
    /**
     * 收起状态下的背景色
     */
    background?: string;
    /**
     * 标题，挂件状态下，只有在收起的时候才会显示标题
     */
    showBack?: boolean;
    /**
     * 显示分享
     */
    showShare?: boolean;
    /**
     * 保持挂件状态
     */
    expand?: boolean;
    /**
     * 挂件图标色
     */
    iconColor?: string;
    /**
     * 折叠状态图标色
     */
    foldIconColor?: string;
    /**
     * 滚动切换临界值
     */
    scrollEdge?: number;
    /**
     * 触发滚动事件的阈值
     */
    scrollThreshold?: number;
    /**
     * 返回前的执行回调
     */
    beforeBack?: (() => void) | (() => Promise<void>);
    /**
     * 自定义返回
     */
    customBack?: () => void;
    /**
     * 显示折叠后的操作区
     */
    displayFoldBar?: boolean;
    /**
     * 手动指定statusBarHeight
     */
    statusBarHeight?: number;
    /**
     * 是否在搜索-1tab
     */
    atSearchTab?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    scrollThreshold: 10,
    scrollEdge: 100,
    displayFoldBar: true,
    showBack: true,
});

const emit = defineEmits<{
    /**
     * 分享
     */
    (event: 'share'): void;
    /**
     * 顶导高度
     * @arg { number } val
     */
    (event: 'total-height', val: number): void;
    /**
     * 系统状态栏顶导高度
     * @arg { number } val
     */
    (event: 'status-bar-height', val: number): void;
    /**
     * 透明度变化
     * @arg { number } val
     */
    (event: 'opacity', val: number): void;
}>();

const { opacityValue, switchHideStyle, switchShowStyle, listenScroll, removeScroll } = useScroll(
    props.scrollThreshold,
    props.scrollEdge,
);

const statusbarHeightComputed = computed(() => (props.atSearchTab ? 0 : props.statusBarHeight));

const { topGap, statusBarHeight: statusBarHeightNative } = useTopGap(statusbarHeightComputed.value);

watch(
    () => props.expand,
    (val) => {
        if (val === false) {
            listenScroll();
        } else {
            removeScroll();
        }
    },
    {
        immediate: true,
    },
);

const expandStatus = computed(() => opacityValue.value > 0.5);
const topGapExpandStatus = computed(() => ({
    pointerEvents: expandStatus.value ? 'auto' : 'none',
}));

const foldBarRef = ref<InstanceType<typeof FoldStatus>>();

watch(
    foldBarRef,
    (val) => {
        if (val) {
            const elRect = val?.$el?.getBoundingClientRect();
            const height = elRect?.height ?? 0;
            const displayHeight = Math.floor(height * 2) / 2;
            emit('total-height', displayHeight);
        }
    },
    {
        flush: 'post',
    },
);

watch(opacityValue, (val) => {
    emit('opacity', 1 - val);
});

emit('status-bar-height', statusBarHeightNative);
</script>

<template>
    <nav class="status-bar">
        <div class="status-bar-placeholder" :style="topGap"></div>
        <PageTitles :at-search-tab="atSearchTab">
            <template #mainTitleTop>
                <!-- @slot 用于联合logo -->
                <slot name="mainTitleTop" />
            </template>
            <template #mainTitle>
                <!-- @slot 用于主slogan -->
                <slot name="mainTitle" />
            </template>
            <template #mainTitleBottom>
                <!-- @slot 用于说明 -->
                <slot name="mainTitleBottom" />
            </template>
        </PageTitles>
        <!-- 展开状态时的占位容器 -->
        <div class="status-bar-main">
            <ExpandStatus
                :style="[switchShowStyle, topGap]"
                :icon-color="iconColor"
                :disabled="expandStatus"
                :custom-back="customBack"
                :before-back="beforeBack"
                :show-back="showBack"
                :at-search-tab="atSearchTab"
            >
                <template #left>
                    <!-- @slot 挂件左侧 -->
                    <slot name="left" />
                </template>
                <template #right>
                    <!-- @slot 挂件右侧 -->
                    <slot name="right" />
                </template>
            </ExpandStatus>
            <FoldStatus
                v-if="!expand"
                ref="foldBarRef"
                :icon-color="foldIconColor"
                :style="[switchHideStyle, topGapExpandStatus, topGap]"
                :title="title"
                :display-fold-bar="displayFoldBar"
                :background="background"
                :show-back="showBack"
                :show-share="showShare"
                :custom-back="customBack"
                :before-back="beforeBack"
                @share="emit('share')"
            >
                <template v-if="$slots['left-switch-fold']" #left-fold>
                    <!-- @slot 吸顶左侧 -->
                    <slot name="left-switch-fold" />
                </template>
                <template v-if="$slots['title-switch-fold']" #title-fold>
                    <!-- @slot 吸顶标题 -->
                    <slot name="title-switch-fold" />
                </template>
                <template v-if="$slots['right-switch-fold']" #right-fold>
                    <!-- @slot 吸顶右侧 -->
                    <slot name="right-switch-fold" />
                </template>
            </FoldStatus>
        </div>
    </nav>
</template>

<style lang="scss" scoped>
@import './var.scss';
.status-bar-main {
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    z-index: 999;
    width: 100%;
    max-width: $status-bar-max-width;
    margin: auto;
}
</style>
